import React from 'react';
import * as PropTypes from 'prop-types';
import FetchService from '../../services/fetchService';
import urlService from '../../services/urlService';
import { simplifyAddress } from '../../utils/simplifyAddress';
import '../../assets/styles/geo-autocomplete.sass';

class ReactMapboxAutocomplete extends React.Component {
  state = {
    error: false,
    errorMsg: '',
    query: this.props.query ?? '',
    queryResults: [],
    resetSearch: this.props.resetSearch ?? false,
  };
  wrapperRef = React.createRef();

  _updateQuery = event => {
    this.setState({ query: event.target.value });
    const { value } = event.target;
    if (this.wrapperRef.current?.dataset) this.wrapperRef.current.dataset.replicatedValue = value;
    const { latitude, longitude } = this.props.geo || {};

    if (value.length > 2) {
      return FetchService.getMapboxResource(
        urlService.getMapboxLocation({
          query: value,
          longitude,
          latitude,
        }),
      )
        .then(response => {
          const { features } = response || {};
          this.setState({
            error: false,
            queryResults: features,
          });
        })
        .catch(err => {
          this.setState({
            error: true,
            errorMsg: 'There was a problem retrieving data from mapbox',
            queryResults: [],
          });
          throw Error(err);
        });
    } else {
      this.setState({
        error: false,
        queryResults: [],
      });
    }
  };

  _resetSearch = () => {
    if (this.state.resetSearch) {
      this.setState({
        query: '',
        queryResults: [],
      });
    } else {
      this.setState({ queryResults: [] });
    }
  };

  _onSuggestionSelect = event => {
    const result = event.target.getAttribute('data-suggestion');
    if (this.state.resetSearch === false) {
      this.setState({ query: result });
    }
    const latitude = event.target.getAttribute('data-lat');
    const longitude = event.target.getAttribute('data-lng');
    const { text, simple } = simplifyAddress({ text: result, latitude, longitude }, this.state.queryResults);
    this.props.onSuggestionSelect({ text, simple });
    const root = document.documentElement;
    root?.style.removeProperty('--form-container-extra-space');
    root?.style.removeProperty('--location-input-dropdown-max-height');
    root?.style.removeProperty('--location-input-dropdown-min-height');
  };

  componentDidMount = () => {
    if (this.wrapperRef.current?.dataset) this.wrapperRef.current.dataset.replicatedValue = this.props.query ?? '';
  };

  componentDidUpdate = (prevProps, prevState) => {
    const { queryResults } = this.state;
    if (queryResults && queryResults !== prevState.queryResults) {
      if (this.props.formContainerRef?.current) {
        const formContainer = this.props.formContainerRef.current;
        const { offsetTop: topParent, offsetHeight: heightParent } = formContainer || {};
        const root = document.documentElement;
        const scrollTop = document.documentElement?.scrollTop;
        const locationInputDropdownTop = topParent + heightParent - scrollTop;
        const height100vh = document.documentElement.clientHeight;
        let dropdownMaxHeight =
          height100vh -
          locationInputDropdownTop -
          root?.style.getPropertyValue('--action-btn-height').replace('px', '') +
          8;
        root?.style.setProperty('--location-input-dropdown-top', `${locationInputDropdownTop}px`);

        const isTightSpace = dropdownMaxHeight < 200;
        if (isTightSpace) {
          const portrait = window.matchMedia('(orientation: portrait)');
          if (portrait) {
            dropdownMaxHeight =
              height100vh - heightParent - root?.style.getPropertyValue('--action-btn-height').replace('px', '') + 8;
            root?.style.setProperty('--location-input-dropdown-max-height', `${dropdownMaxHeight}px`);
            root?.style.setProperty('--location-input-dropdown-min-height', '0');
          }
          root?.style.setProperty('--form-container-extra-space', this.props.inputId === 'pickup' ? '35px' : '140px');
          this.props.formContainerRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  render() {
    return (
      <div className={`react-mapbox-ac-container${this.state.query ? '' : ' no-query'}`}>
        <div ref={ref => (this.wrapperRef.current = ref)} className="form-input grow-wrap">
          <textarea
            placeholder={this.props.placeholder || 'Search'}
            id={this.props.inputId}
            onClick={this.props.inputOnClick}
            onKeyUp={this.props.inputOnKeyUp}
            onBlur={this.props.inputOnBlur}
            onFocus={this.props.inputOnFocus}
            className={
              this.props.inputClass ? this.props.inputClass + ' react-mapbox-ac-input' : 'react-mapbox-ac-input'
            }
            onChange={this._updateQuery}
            value={this.state.query}
            ref={ref => (this.props.inputRef.current = ref)}
            autoComplete="off"
            autoFocus={true}
          />
        </div>
        <span>
          <div
            className="react-mapbox-ac-menu"
            style={this.state.queryResults.length > 0 || this.state.error ? { display: 'block' } : { display: 'none' }}
            onClick={this._resetSearch}
          >
            {this.state.queryResults.map((place, i) => {
              const place_name = place.place_name.replace(', United States', '');
              return (
                <div
                  className="react-mapbox-ac-suggestion"
                  onClick={this._onSuggestionSelect}
                  key={i}
                  data-suggestion={place_name}
                  data-lng={place.center[0]}
                  data-lat={place.center[1]}
                  data-text={place.text}
                >
                  {place_name}
                </div>
              );
            })}

            {this.state.error && <div className="react-mapbox-ac-suggestion">{this.state.errorMsg}</div>}
          </div>
        </span>
      </div>
    );
  }
}

ReactMapboxAutocomplete.defaultProps = {
  inputId: null,
  inputOnKeyUp: null,
  inputOnFocus: null,
  inputOnBlur: null,
  inputOnClick: null,
};

ReactMapboxAutocomplete.propTypes = {
  inputId: PropTypes.string,
  inputOnKeyUp: PropTypes.func,
  inputOnFocus: PropTypes.func,
  inputOnBlur: PropTypes.func,
  inputOnClick: PropTypes.func,
  inputRef: PropTypes.object,
  formContainerRef: PropTypes.object,
  inputClass: PropTypes.string,
  placeholder: PropTypes.string,
  onSuggestionSelect: PropTypes.func.isRequired,
  country: PropTypes.string,
  query: PropTypes.string,
  resetSearch: PropTypes.bool,
  geo: PropTypes.object,
};

export default ReactMapboxAutocomplete;
